import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Plus,
  LogOut,
  Trash2,
  Bot,
  Edit2,
  MoreVertical,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../stores/auth";
import { useChatStore } from "../stores/chat";
import logoFull from "../assets/images/logo_full.png";
import defaultBotAvatar from "../assets/images/default-bot-avatar.png";

export default function Sidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const {
    threads,
    fetchThreads,
    setCurrentThread,
    currentThread,
    deleteThread,
    selectedAssistant,
    createThread,
    setSelectedAssistant,
    renameThread,
  } = useChatStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameThreadId, setRenameThreadId] = useState<string | null>(null);
  const [newThreadName, setNewThreadName] = useState("");
  const renameInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus rename input when it appears
  useEffect(() => {
    if (renameThreadId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renameThreadId]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        await Promise.all([fetchThreads()]);
      } catch (error: any) {
        console.error("Failed to load data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [fetchThreads]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleNewChat = () => {
    setSelectedAssistant(null);
    setCurrentThread(null);
    navigate("/new-chat");
  };

  const handleCreateChat = async () => {
    if (!selectedAssistant || isCreating) return;

    setIsCreating(true);
    try {
      const threadId = await createThread(selectedAssistant.id);
      navigate(`/chat/${threadId}`);
    } catch (error: any) {
      console.error("Failed to create chat:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const toggleDropdown = (threadId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveDropdown(activeDropdown === threadId ? null : threadId);
  };

  const handleDeleteThread = async (threadId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveDropdown(null);

    try {
      const isCurrentThread = currentThread?.id === threadId;
      await deleteThread(threadId);

      if (isCurrentThread) {
        setCurrentThread(null);
        navigate("/new-chat");
      }
    } catch (error) {
      console.error("Failed to delete thread:", error);
    }
  };

  const handleStartRename = (
    threadId: string,
    currentName: string | undefined,
    e: React.MouseEvent
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveDropdown(null);
    setRenameThreadId(threadId);
    setNewThreadName(currentName || threadId.substring(0, 8));
  };

  const handleCancelRename = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setRenameThreadId(null);
    setNewThreadName("");
  };

  const handleSaveRename = async (threadId: string, e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!newThreadName.trim() || isRenaming) return;

    setIsRenaming(true);
    try {
      await renameThread(threadId, newThreadName.trim());
      setRenameThreadId(null);
      setNewThreadName("");
    } catch (error) {
      console.error("Failed to rename thread:", error);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleRenameKeyDown = (
    threadId: string,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveRename(threadId);
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancelRename();
    }
  };

  // Filter threads for selected assistant
  const filteredThreads = selectedAssistant
    ? threads.filter((thread) => thread.bot_id === selectedAssistant.id)
    : [];

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center w-[250px] bg-[#0F1524]">
        <p className="text-white">{t("common.loading")}</p>
      </div>
    );
  }

  return (
    <div className="w-[250px] h-full flex flex-col bg-[#0F1524] border-r border-[#1A2235]">
      {/* Logo and Organization */}
      <div className="py-2 border-b border-[#1A2235]">
        <Link
          to="/new-chat"
          className="flex items-center justify-center w-full"
        >
          <img
            src={logoFull}
            alt={t("common.appName")}
            className="w-full object-contain max-h-16"
            style={{
              imageRendering: "crisp-edges",
              WebkitBackfaceVisibility: "hidden",
              backfaceVisibility: "hidden",
              filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.2))",
            }}
          />
        </Link>
      </div>

      {/* Selected Assistant Info */}
      {selectedAssistant && (
        <div className="px-3 py-2 border-b border-[#1A2235]">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-[#D6E2E8] to-[#ECF3F5] flex items-center justify-center"
              style={{
                backgroundImage:
                  "linear-gradient(146.84deg, #D6E2E8 -17.54%, #ECF3F5 80.24%)",
                filter: "drop-shadow(0px 0.77px 5.07px rgba(0,0,0,0.25))",
              }}
            >
              <img
                src={defaultBotAvatar}
                alt={t("common.botAvatar")}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-white font-medium text-sm truncate">
                {selectedAssistant.name}
              </span>
              <span className="text-gray-400 text-xs truncate max-w-[180px]">
                {selectedAssistant.description || t("common.aiAssistant")}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Buttons */}
      <div className="p-2 space-y-2">
        <button
          onClick={handleNewChat}
          className="sidebar-button bg-gradient-to-r from-[#00C2CB] to-[#6A98F0] hover:opacity-90"
        >
          <Bot size={18} />
          <span>{t("new_chat.title")}</span>
        </button>

        {selectedAssistant && (
          <button
            onClick={handleCreateChat}
            disabled={isCreating}
            className="sidebar-button-secondary"
          >
            <Plus size={18} />
            <span>{t("new_chat.create_chat")}</span>
          </button>
        )}
      </div>

      {/* Chats List */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {!selectedAssistant ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-3">
            <Bot size={32} className="text-[#1A2235] mb-3" />
            <p className="text-gray-400 text-sm">
              {t("new_chat.error.no_assistants")}
            </p>
          </div>
        ) : filteredThreads.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-3">
            <MessageSquare size={32} className="text-[#1A2235] mb-3" />
            <p className="text-gray-400 text-sm">{t("chat.select_chat")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredThreads.map((thread) => (
              <div key={thread.id} className="relative">
                {renameThreadId === thread.id ? (
                  <form
                    onSubmit={(e) => handleSaveRename(thread.id, e)}
                    className="flex items-center gap-2 p-2 rounded-lg bg-[#1A2235]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MessageSquare
                      size={16}
                      className="text-gray-400 shrink-0"
                    />
                    <input
                      ref={renameInputRef}
                      type="text"
                      value={newThreadName}
                      onChange={(e) => setNewThreadName(e.target.value)}
                      onKeyDown={(e) => handleRenameKeyDown(thread.id, e)}
                      className="flex-1 bg-[#253045] text-white text-sm px-2 py-1 rounded focus:outline-none focus:ring-1 focus-[#00C2CB]"
                      disabled={isRenaming}
                      maxLength={50}
                    />
                    <button
                      type="submit"
                      disabled={isRenaming || !newThreadName.trim()}
                      className="p-1 rounded-lg text-[#00C2CB] disabled:text-gray-600"
                      title={t("common.save")}
                    >
                      {t("common.save")}
                    </button>
                    <button
                      onClick={handleCancelRename}
                      disabled={isRenaming}
                      className="p-1 rounded-lg text-gray-400"
                      title={t("common.cancel")}
                    >
                      {t("common.cancel")}
                    </button>
                  </form>
                ) : (
                  <Link
                    to={`/chat/${thread.id}`}
                    className={`flex items-center gap-2 p-2 rounded-lg hover:bg-[#1A2235] transition-colors ${
                      currentThread?.id === thread.id ? "bg-[#1A2235]" : ""
                    }`}
                  >
                    <MessageSquare size={16} className="text-gray-400" />
                    <span className="text-white text-sm truncate flex-1">
                      {thread.name ||
                        `New Chat ${new Date(
                          thread.created_at
                        ).toTimeString()}`}
                    </span>
                    <button
                      onClick={(e) => toggleDropdown(thread.id, e)}
                      className="p-1 rounded-lg hover:bg-[#253045] text-gray-400 hover:text-white"
                      aria-label={t("common.options")}
                    >
                      <MoreVertical size={14} />
                    </button>

                    {activeDropdown === thread.id && (
                      <div
                        ref={dropdownRef}
                        className="absolute right-0 top-10 z-10 min-w-[120px] py-1 bg-[#1A2235] border border-[#253045] rounded-lg shadow-lg"
                      >
                        <button
                          onClick={(e) =>
                            handleStartRename(thread.id, thread.name, e)
                          }
                          className="flex items-center gap-2 w-full px-3 py-2 text-left text-white text-sm hover:bg-[#253045]"
                        >
                          <Edit2 size={14} />
                          <span>{t("common.rename")}</span>
                        </button>
                        <button
                          onClick={(e) => handleDeleteThread(thread.id, e)}
                          className="flex items-center gap-2 w-full px-3 py-2 text-left text-[#EF4444] text-sm hover:bg-[#253045]"
                        >
                          <Trash2 size={14} />
                          <span>{t("common.delete")}</span>
                        </button>
                      </div>
                    )}
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Logout Button */}
      <div className="px-3 py-2 border-t border-[#1A2235]">
        <button
          onClick={handleLogout}
          className="flex items-center justify-center gap-2 text-[#EF4444] p-2 rounded-lg hover:bg-[#1A2235] w-full text-xs"
        >
          <LogOut size={14} />
          <span>{t("login.logout")}</span>
        </button>
      </div>
    </div>
  );
}
